"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import Link from "next/link";
import { useLocale } from "next-intl";
import {
  Excalidraw,
  restoreElements,
  restoreAppState,
} from "@/excalidraw";
import type {
  ExcalidrawImperativeAPI,
  AppState,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@/excalidraw/types";

import "@/excalidraw/css/app.scss";
import "@/excalidraw/css/styles.scss";
import "@/excalidraw/fonts/fonts.css";

import { DRAW_STORAGE_KEYS } from "./constants";
import { useDrawTheme } from "./useDrawTheme";
import styles from "./draw.module.scss";

const SAVE_DEBOUNCE_MS = 800;

function getStoredScene(): { elements: string; appState: string } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAW_STORAGE_KEYS.SCENE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { elements?: string; appState?: string };
    if (typeof parsed?.elements === "string" && typeof parsed?.appState === "string")
      return { elements: parsed.elements, appState: parsed.appState };
  } catch {
    // ignore
  }
  return null;
}

function saveScene(elements: readonly unknown[], appState: AppState) {
  try {
    window.localStorage.setItem(
      DRAW_STORAGE_KEYS.SCENE,
      JSON.stringify({
        elements: JSON.stringify(elements),
        appState: JSON.stringify(appState),
      })
    );
  } catch {
    // ignore quota etc.
  }
}

/** Map app locale to Excalidraw langCode (e.g. "en" -> "en", "es" -> "es-ES"). */
function localeToLangCode(locale: string): string {
  const map: Record<string, string> = {
    en: "en",
    es: "es-ES",
    fr: "fr-FR",
    de: "de-DE",
    it: "it-IT",
    pt: "pt-BR",
    ru: "ru-RU",
    ja: "ja-JP",
    zh: "zh-CN",
    ko: "ko-KR",
    hi: "hi-IN",
    ar: "ar-SA",
    pl: "pl-PL",
    nl: "nl-NL",
    tr: "tr-TR",
    vi: "vi-VN",
    id: "id-ID",
    th: "en",
    sv: "sv-SE",
    cs: "cs-CZ",
    uk: "uk-UA",
    el: "el-GR",
    ro: "ro-RO",
    hu: "hu-HU",
    fa: "fa-IR",
    he: "he-IL",
  };
  return map[locale] ?? (locale.length <= 2 ? locale : locale.split("-")[0]) ?? "en";
}

export default function DrawEditorClient() {
  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { editorTheme } = useDrawTheme();
  const locale = useLocale();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");

  const [initialDataPromise] = useState(() => {
    return new Promise<ExcalidrawInitialDataState | null>((resolve) => {
      const stored = getStoredScene();
      if (!stored) {
        resolve(null);
        return;
      }
      try {
        const elements = restoreElements(
          JSON.parse(stored.elements) as Parameters<typeof restoreElements>[0],
          null,
          { repairBindings: true }
        );
        const appState = restoreAppState(JSON.parse(stored.appState), null);
        resolve({ elements, appState } as ExcalidrawInitialDataState);
      } catch {
        resolve(null);
      }
    });
  });

  const onChange = useCallback(
    (elements: readonly unknown[], appState: AppState, _files: BinaryFiles) => {
      setSaveStatus("saving");
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveScene(elements, appState);
        setSaveStatus("saved");
        saveTimeoutRef.current = null;
        setTimeout(() => setSaveStatus("idle"), 2000);
      }, SAVE_DEBOUNCE_MS);
    },
    []
  );

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as unknown as { EXCALIDRAW_ASSET_PATH?: string }).EXCALIDRAW_ASSET_PATH =
        window.location.origin;
    }
  }, []);

  const renderTopLeftUI = useCallback(
    (isMobile: boolean, _appState: UIAppState) => (
      <div className={styles.topLeftUI}>
        <Link href={`/${locale}`} className={styles.backLink} aria-label="Back to home">
          ← {isMobile ? "" : "Back"}
        </Link>
        {saveStatus === "saved" && (
          <span className={styles.saveBadge} role="status" aria-live="polite">
            Saved
          </span>
        )}
        {saveStatus === "saving" && (
          <span className={styles.saveBadge} role="status">
            Saving…
          </span>
        )}
      </div>
    ),
    [locale, saveStatus]
  );

  return (
    <div className={styles.wrapper}>
      <Excalidraw
        excalidrawAPI={(api) => {
          apiRef.current = api;
        }}
        initialData={initialDataPromise}
        onChange={onChange}
        theme={editorTheme}
        langCode={localeToLangCode(locale)}
        renderTopLeftUI={renderTopLeftUI}
        UIOptions={{
          dockedSidebarBreakpoint: 0,
          canvasActions: {
            toggleTheme: true,
            loadScene: true,
            export: { saveFileToDisk: true },
            saveAsImage: true,
            clearCanvas: true,
            changeViewBackgroundColor: true,
          },
          tools: {
            image: true,
          },
        }}
        handleKeyboardGlobally={true}
        autoFocus={true}
        name="tradeics-draw"
      />
    </div>
  );
}
